import type { QueryKey } from '@tanstack/react-query';

import {
  DISCOVER_TRACK_KEY,
  MY_GROUPS_KEY,
  MY_RECOMMENDATIONS_KEY,
  groupMembersKey,
  groupRequestsKey,
} from '@/constants/queryKeys';

/**
 * A8 — a push notification tells the app that server state changed, but the app
 * never acted on it: `notificationReceived` dropped `notification.data` on the
 * floor, so a circle you were just accepted into, a join request that just
 * landed, or a new sound in a circle stayed invisible until the next manual
 * pull-to-refresh.
 *
 * This maps a push payload to the exact set of caches that went stale. It is
 * deliberately pure (no QueryClient, no React) so every branch is unit-testable,
 * and deliberately NARROW: a blanket `invalidateQueries()` would refetch the
 * discover feed on every notification and reshuffle the deck under the user's
 * finger.
 *
 * Returned keys are PREFIXES — `invalidateQueries({ queryKey })` matches every
 * cache entry starting with them (see `constants/queryKeys.ts`).
 *
 * Payload types mirror the backend triggers in `src/services/notification-triggers.ts`.
 */
export function keysToInvalidate(data: Record<string, string> | undefined): QueryKey[] {
  if (!data?.type) return [];

  // Circle ids are 1-based; anything else can only come from a malformed
  // payload, and a key holding NaN would never match a real cache entry.
  const parsed = Number.parseInt(data.groupId ?? '', 10);
  const groupId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

  switch (data.type) {
    // Someone asked to join a circle I administrate → the pending-requests list
    // grew, and the circle card shows a request badge.
    case 'join_request':
      return [...(groupId ? [groupRequestsKey(groupId)] : []), MY_GROUPS_KEY];

    // My join request was accepted → I'm now a member: the circle appears in my
    // list, and its member roster changed.
    case 'member_accepted':
      return [...(groupId ? [groupMembersKey(groupId)] : []), MY_GROUPS_KEY];

    // A new sound landed in a circle → the discover feed has an extra card, and
    // the circle's unseen badge moved.
    case 'new_group_recommendation':
      return [DISCOVER_TRACK_KEY, MY_GROUPS_KEY];

    // Someone reacted to / rebounded one of MY recos → the impact numbers on
    // the My Recos screen are stale.
    case 'reaction_received':
    case 'rebound_received':
      return [MY_RECOMMENDATIONS_KEY];

    // The daily "here's what to listen to" push points at the feed.
    case 'daily_recommendations':
      return [DISCOVER_TRACK_KEY];

    // 'test' pushes and any future type the app doesn't know yet: invalidate
    // nothing rather than guess.
    default:
      return [];
  }
}

/**
 * discover-card-polish — quelle reco voir son compteur de réponses incrémenté.
 *
 * Séparé de `keysToInvalidate` parce que ce n'est PAS une invalidation : le feed
 * est un `useInfiniteQuery`, l'invalider refetcherait toutes les pages chargées
 * et rebattrait le deck sous le doigt (l'avertissement en tête de ce fichier).
 * On écrit le nouveau chiffre dans le cache ; cette fonction dit seulement lequel.
 *
 * Pure, comme le reste du fichier : le hook fait l'écriture.
 */
export interface ReplyToBump {
  recoId: number;
  /** Absent d'une charge utile antérieure au champ : on s'abstient plutôt que de deviner. */
  groupId: number | undefined;
}

/** Les ids commencent à 1 : tout le reste vient d'une charge utile malformée. */
const positiveId = (raw: string | undefined): number | undefined => {
  const id = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

export function recoToBump(data: Record<string, string> | undefined): ReplyToBump | null {
  if (data?.type !== 'reply_received') return null;
  const recoId = positiveId(data.recommendationId);
  // Incrémenter une reco inventée vaut moins que ne rien faire.
  if (recoId === undefined) return null;
  // comment-circle-scope — le cercle vient du serveur (`notification-triggers`).
  // Sans lui le compteur ne bouge pas : un chiffre vieux vaut mieux qu'un
  // chiffre juste affiché dans le mauvais cercle.
  return { recoId, groupId: positiveId(data.groupId) };
}
